import { TooltipPrimeReact } from "@finanedi.es/storybook"
import { faXmark } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO } from "api/DTOs/ConciliacionConciliarMovimientosCommandDto"
import { EntidadConciliableReadingDtoProps } from "api/DTOs/EntidadConciliableReadingDto"
import { TFunction } from "i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { frontendDateFormat } from "utils/formatDate"
import { TabItem } from "../constants/constants"
import TextWithTooltip from "./TextWithToolip"

const PANEL_CONCILIABLES_COLUMNS = (t: TFunction, tabSelected: TabItem) => [
  {
    key: "identificador",
    field: "identificador",
    sortField: "Identificador",
    sortable: false,
    header: `${t(
      "bancos.conciliacion.rightColumn.conciliableTable.numberLabel"
    )}`,
    body: (rowData: EntidadConciliableReadingDtoProps) => {
      if (rowData?.asientoContable) {
        return (
          <div className='tw-flex tw-flex-col'>
            <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
              {t(
                "bancos.conciliacion.rightColumn.conciliableTable.accountingLabel"
              )}{" "}
              {rowData?.asientoContable?.numero || "-"}
            </p>
            <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
              {t("bancos.conciliacion.rightColumn.conciliableTable.flowLabel")}{" "}
              {rowData?.identificador}
            </p>
          </div>
        )
      }
      return rowData?.identificador
    },
  },
  {
    key: "fecha",
    field: "fecha",
    sortField: "Fecha",
    sortable: false,
    header: `${t(
      "bancos.conciliacion.rightColumn.conciliableTable.emissionDateLabel"
    )}`,

    body: ({ fecha }: EntidadConciliableReadingDtoProps) =>
      fecha ? frontendDateFormat(new Date(fecha)) : "-",
  },
  {
    key: "razonSocial",
    field: "razonSocial",
    sortField: "Empresa.RazonSocial",
    sortable: false,
    header: `${t(
      "bancos.conciliacion.rightColumn.conciliableTable.companyNameLabel"
    )}`,
    body: (rowData?: EntidadConciliableReadingDtoProps) =>
      rowData?.empresa?.razonSocial ?? "-",
    className: "!tw-w-1/5",
  },
  {
    key: "descripcion",
    field: "descripcion",
    sortField: "Descripcion",
    sortable: false,
    header: `${t(
      "bancos.conciliacion.rightColumn.conciliableTable.descriptionLabel"
    )}`,
    body: (rowData?: EntidadConciliableReadingDtoProps) => {
      let description = rowData?.descripcion ?? ""
      if (typeof rowData?.tipo === "number" && tabSelected.TYPE) {
        description = `${tabSelected.TYPE[Number(rowData.tipo)]}${
          rowData?.descripcion ? ` - ${rowData.descripcion}` : ""
        }`
      }
      if (rowData?.asientoContable) {
        return (
          <div className='tw-flex tw-flex-col !tw-text-sm'>
            <div className={`descripcionTooltip-${rowData.id}`}>
              <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
                {t(
                  "bancos.conciliacion.rightColumn.conciliableTable.accountingLabel"
                )}{" "}
                {rowData?.asientoContable?.descripcion || "-"}
              </p>
              <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
                {t(
                  "bancos.conciliacion.rightColumn.conciliableTable.flowLabel"
                )}{" "}
                {description}
              </p>
            </div>
            <TooltipPrimeReact
              id={`descripcionTooltip-${rowData.id}`}
              target={`.descripcionTooltip-${rowData.id}`}
              position='left'
            >
              <div className='tw-flex tw-flex-col'>
                <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
                  {t(
                    "bancos.conciliacion.rightColumn.conciliableTable.accountingLabel"
                  )}{" "}
                  {rowData?.asientoContable?.descripcion || "-"}
                </p>
                <p className='tw-text-ellipsis tw-truncate !tw-text-sm'>
                  {t(
                    "bancos.conciliacion.rightColumn.conciliableTable.flowLabel"
                  )}{" "}
                  {description}
                </p>
              </div>
            </TooltipPrimeReact>
          </div>
        )
      }
      return description
    },
    className:
      "tw-text-base tw-text-text tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-flex-1 !tw-w-1/5 tw-max-w-0",
  },
  ...(tabSelected.KEY ===
  CONCILIACION_CONCILIAR_MOVIMIENTOS_COMMAND_DTO.FLUJO_TESORERIA_IDS
    ? [
        {
          key: "documento",
          field: "documento",
          sortField: "Documento",
          sortable: false,
          header: `${t(
            "bancos.conciliacion.rightColumn.conciliableTable.documentLabel"
          )}`,
          body: (rowData?: EntidadConciliableReadingDtoProps) => (
            <TextWithTooltip
              id={rowData?.id}
              text={rowData?.asientoContable?.documento}
            />
          ),
          className:
            "tw-text-ellipsis tw-overflow-hidden tw-whitespace-nowrap tw-flex-1  !tw-w-1/5 tw-max-w-0",
        },
      ]
    : []),
  {
    key: "importe",
    field: "importe",
    sortField: "Importe",
    sortable: true,
    className: "!tw-text-end",
    alignCol: "right",
    header: `${t(
      "bancos.conciliacion.rightColumn.conciliableTable.amountLabel"
    )}`,
    body: (rowData?: EntidadConciliableReadingDtoProps) =>
      frontendCurrency(rowData?.importe ?? 0),
  },
]

export default PANEL_CONCILIABLES_COLUMNS

export const CONCILIACION_MANUAL_SELECTED_COLUMNS_RIGHT = (
  t: TFunction,
  unSelectedRow: (rowData: EntidadConciliableReadingDtoProps) => void
) => {
  const columns = [
    {
      key: "unselected",
      field: "unselected",

      body: (rowData: EntidadConciliableReadingDtoProps) => (
        <span
          className='tw-flex tw-justify-center tw-p-2'
          onClick={() => unSelectedRow(rowData)}
        >
          <FontAwesomeIcon
            icon={faXmark}
            className='tw-text-danger tw-cursor-pointer'
          />
        </span>
      ),
      style: { width: "4%" },
    },
    {
      key: "identificador",
      field: "identificador",
      header: `${t("bancos.conciliacion.manual.labelNum")}`,
      style: { width: "10%" },
    },
    {
      key: "fecha",
      field: "fecha",
      style: { width: "10%" },
      header: `${t("bancos.conciliacion.manual.labelEmisionDate")}`,
      body: ({ fecha }: EntidadConciliableReadingDtoProps) =>
        fecha ? frontendDateFormat(new Date(fecha)) : "-",
    },
    {
      key: "razonSocial",
      field: "razonSocial",
      header: `${t("bancos.conciliacion.manual.labelCompanyName")}`,
      body: ({ empresa }: EntidadConciliableReadingDtoProps) =>
        empresa?.razonSocial ?? "-",
      style: { width: "20%" },
    },
    {
      key: "descripcion",
      field: "descripcion",
      header: `${t("bancos.conciliacion.manual.labelDescription")}`,
      body: ({ descripcion }: EntidadConciliableReadingDtoProps) =>
        descripcion ?? "-",
      style: { width: "20%" },
    },
    {
      key: "importe",
      field: "importe",
      header: `${t("bancos.conciliacion.manual.labelDescription")}`,
      body: ({ importe }: EntidadConciliableReadingDtoProps) =>
        importe ? frontendCurrency(importe) : "-",
      style: { width: "10%" },
      className: "!tw-text-end",
      alignCol: "right",
    },
  ]
  return columns
}
